
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { UserProfile, SavedOutfit, Lookbook, SavedContent, AppPage, StyleAgentConfig } from '../types';
import { MessageSquareIcon, FileTextIcon, PaletteIcon, LockIcon, SparklesIcon, CheckCircleIcon, RotateCcwIcon, BotIcon, PlusIcon, Trash2Icon, SearchIcon, PlayIcon, MailIcon } from '../components/icons';
import StyleBot from '../components/StyleBot';
import ColorAnalysisModal from '../components/ColorAnalysisModal';
import StyleAgentModal from '../components/StyleAgentModal';
import { generateStylePlan, findSimilarItems, getFashionAdvice } from '../services/geminiService';
import { checkStyleAgentQuota } from '@shared/services/userService';
import { emailService } from '@shared/services/emailService';
import { STYLE_AGENT_LIMITS } from '../constants';
import Spinner from '../components/Spinner';
import StyleWizard from '../components/StyleWizard';
import { getFriendlyErrorMessage } from '../lib/utils';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { ShoppingAssistantChat } from '../components/ShoppingAssistant/ShoppingAssistantChat';
import { hasAssistantAccess, hasMembershipAccess } from '@shared/services/userService';

interface StylingAdvisorPageProps {
    userProfile: UserProfile;
    savedOutfits: SavedOutfit[];
    lookbooks: Lookbook[];
    savedContent: SavedContent[];
    onSaveContent: (contentToSave: Omit<SavedContent, 'id' | 'createdAt'>) => void;
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
    setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
    initialAgentId?: string;
    isGuest: boolean;
    initialQuery?: string;
    onAddToBasket: (product: any) => void;
}

interface Agent {
    id: string;
    nameKey: string;
    descriptionKey: string;
    requiredMembership?: 'basic' | 'premium' | 'advanced';
}

const agents: Agent[] = [
    { id: 'fashionExpert', nameKey: 'stylingAdvisorPage_agent_fashionExpert_name', descriptionKey: 'stylingAdvisorPage_agent_fashionExpert_description', requiredMembership: 'basic' },
    { id: 'minimalistStylist', nameKey: 'stylingAdvisorPage_agent_minimalistStylist_name', descriptionKey: 'stylingAdvisorPage_agent_minimalistStylist_description', requiredMembership: 'basic' },
    { id: 'styleQuestionnaire', nameKey: 'stylingAdvisorPage_agent_styleQuestionnaire_name', descriptionKey: 'stylingAdvisorPage_agent_styleQuestionnaire_description', requiredMembership: 'basic' },
    { id: 'business', nameKey: 'stylingAdvisorPage_agent_business_name', descriptionKey: 'stylingAdvisorPage_agent_business_description', requiredMembership: 'premium' },
    { id: 'vogueEditor', nameKey: 'stylingAdvisorPage_agent_vogueEditor_name', descriptionKey: 'stylingAdvisorPage_agent_vogueEditor_description', requiredMembership: 'premium' },
    { id: 'glamour', nameKey: 'stylingAdvisorPage_agent_glamour_name', descriptionKey: 'stylingAdvisorPage_agent_glamour_description', requiredMembership: 'advanced' },
];

type PlanDuration = 'weekly' | 'monthly' | 'quarterly';

const hasAccess = (requiredLevel: 'basic' | 'premium' | 'advanced' | undefined, userLevel: 'basic' | 'premium' | 'advanced' | 'nextgen' | undefined = 'basic', expiryDateStr?: string): boolean => {
    if (!requiredLevel || requiredLevel === 'basic') return true;
    if (userLevel === 'basic') return false;

    const expiryDate = expiryDateStr ? new Date(expiryDateStr + 'T23:59:59') : null;
    if (expiryDate && expiryDate < new Date()) return false;

    if (userLevel === 'advanced' || userLevel === 'nextgen') return true;
    if (userLevel === 'premium' && requiredLevel === 'premium') return true;

    return false;
};

const PLAN_PERMISSIONS: Record<PlanDuration, 'basic' | 'premium' | 'advanced'> = {
    weekly: 'basic',
    monthly: 'premium',
    quarterly: 'advanced'
};

const StylingAdvisorPage: React.FC<StylingAdvisorPageProps> = ({
    userProfile,
    savedOutfits,
    lookbooks,
    onSaveContent,
    onNavigate,
    setUserProfile,
    initialAgentId,
    isGuest,
    savedContent,
}) => {
    const { t, lang } = useTranslation();

    useSEO(
        "Personal Style Advisory & Color Analysis",
        "Chat with your AI stylist. Get a personalized style plan, discover your seasonal color palette, and receive expert fashion advice tailored to you.",
        ["style advisory", "color analysis online", "seasonal color palette", "personal styling plan", "fashion chatbot", "body type analysis", "seasonal color analysis", "find my color season", "skin tone analysis"]
    );

    const [activeSection, setActiveSection] = useState<'shopping_assistant' | 'chat' | 'plan' | 'color_analysis' | 'agents' | 'history'>('shopping_assistant');
    const [selectedAgentId, setSelectedAgentId] = useState<string>(isGuest ? 'styleQuestionnaire' : 'fashionExpert');
    const [planDuration, setPlanDuration] = useState<PlanDuration>('weekly');
    const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
    const [planError, setPlanError] = useState<string | null>(null);
    const [generatedPlan, setGeneratedPlan] = useState<string | null>(null);
    const [isPlanSaved, setIsPlanSaved] = useState(false);
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);

    // Chat state
    const [chatOpen, setChatOpen] = useState(true);
    const [isRunningAgents, setIsRunningAgents] = useState<Record<string, boolean>>({});

    const handleRunAgent = async (agentId: string) => {
        const agent = userProfile.styleAgents?.find(a => a.id === agentId);
        if (!agent || isRunningAgents[agentId]) return;

        setIsRunningAgents(prev => ({ ...prev, [agentId]: true }));

        try {
            let results = agent.cachedResults || [];
            let cacheLeft = agent.cacheIntervalsLeft || 0;

            if (cacheLeft === 0 || !agent.cachedResults || agent.cachedResults.length === 0) {
                // Perform new search
                const searchPrompt = `Find ${agent.focusArea} items for ${agent.targetSeason} that complement my style.`;
                const searchResult = await findSimilarItems([], searchPrompt, userProfile);
                results = searchResult.results;
                cacheLeft = 4; // Reset cache
            } else {
                cacheLeft -= 1; // Consume cache interval
            }

            // Generate summary and send email
            const summaryPrompt = `Based on these items: ${results.map((i: any) => i.name).join(', ')}, provide a brief style summary for my ${agent.focusArea} agent.`;
            const summary = await getFashionAdvice(summaryPrompt, `Agent: ${agent.name}`, lang, userProfile);

            await emailService.sendEmail(
                userProfile.email || '',
                t('styleAgent_emailSubject', { agentName: agent.name }),
                `<div>${summary}</div>`,
                summary
            );

            // Update agent state in profile
            setUserProfile(prev => ({
                ...prev,
                styleAgents: (prev.styleAgents || []).map(a =>
                    a.id === agentId
                        ? {
                            ...a,
                            cachedResults: results,
                            cacheIntervalsLeft: cacheLeft,
                            lastRun: new Date().toISOString(),
                            lastEmailSent: new Date().toISOString()
                        }
                        : a
                )
            }));

            // Sync findings to Shopping Assistant if eligible
            if (hasAssistantAccess(userProfile)) {
                const assistantMsg = {
                    id: `agent-finding-${agentId}-${Date.now()}`,
                    role: 'model',
                    text: `${t('shoppingAssistant_agent_prefix', { agentName: agent.name })} ${summary}`,
                    timestamp: Date.now(),
                    payload: {
                        type: 'products',
                        data: results
                    }
                };

                const savedHistory = localStorage.getItem('shoppingAssistantHistory');
                const history = savedHistory ? JSON.parse(savedHistory) : [];
                localStorage.setItem('shoppingAssistantHistory', JSON.stringify([...history, assistantMsg]));
            }

            alert(t('styleAgent_emailSent'));
        } catch (error) {
            console.error('Error running agent:', error);
            alert(t('stylingAdvisorPage_planError'));
        } finally {
            setIsRunningAgents(prev => ({ ...prev, [agentId]: false }));
        }
    };

    // Handle initial agent selection or section from navigation params
    useEffect(() => {
        if (initialAgentId) {
            const agentExists = agents.some(a => a.id === initialAgentId);
            if (agentExists) {
                // If guest tries to access restricted agent via params, force questionnaire
                if (isGuest && initialAgentId !== 'styleQuestionnaire') {
                    setSelectedAgentId('styleQuestionnaire');
                } else {
                    setSelectedAgentId(initialAgentId);
                }
                setActiveSection('chat');
                setChatOpen(true);
            }
        }

        // Handle direct section navigation (e.g. from Shopping Assistant trigger)
        const params = new URLSearchParams(window.location.search);
        const section = params.get('section');
        if (section && ['shopping_assistant', 'chat', 'plan', 'color_analysis', 'agents', 'history'].includes(section)) {
            setActiveSection(section as any);
        }
    }, [initialAgentId, isGuest]);

    // Force selection if user becomes guest
    useEffect(() => {
        if (isGuest && selectedAgentId !== 'styleQuestionnaire') {
            setSelectedAgentId('styleQuestionnaire');
        }
    }, [isGuest, selectedAgentId]);

    const handleAgentSelect = (agentId: string) => {
        setSelectedAgentId(agentId);
        setChatOpen(true);
    };

    const handleGeneratePlan = async () => {
        // Double check permissions before generating
        const requiredLevel = PLAN_PERMISSIONS[planDuration];
        if (requiredLevel !== 'basic' && !hasAccess(requiredLevel === 'premium' ? 'premium' : 'advanced', userProfile.membershipLevel as any, userProfile.membershipExpiry)) {
            setPlanError(t(requiredLevel === 'premium' ? 'feature_locked_premium_generic' : 'feature_locked_advanced_generic'));
            return;
        }

        setIsGeneratingPlan(true);
        setPlanError(null);
        setGeneratedPlan(null);
        setIsPlanSaved(false);
        try {
            const { plan } = await generateStylePlan(userProfile, savedOutfits, lang, planDuration);
            setGeneratedPlan(plan);
        } catch (error) {
            setPlanError(getFriendlyErrorMessage(error, t('stylingAdvisorPage_planError'), lang, t));
        } finally {
            setIsGeneratingPlan(false);
        }
    };

    const handleSavePlan = () => {
        if (generatedPlan) {
            onSaveContent({
                type: 'plan',
                title: t('stylingAdvisorPage_planSavedTitle', { duration: t(`stylingAdvisorPage_planDuration_${planDuration}` as any) }),
                content: generatedPlan,
            });
            setIsPlanSaved(true);
        }
    };

    const handleSaveAgent = (config: StyleAgentConfig) => {
        setUserProfile(prev => ({
            ...prev,
            styleAgents: [...(prev.styleAgents || []), config]
        }));
    };

    const handleSaveColorAnalysis = (attributes: any) => {
        setUserProfile(prev => ({
            ...prev,
            ...attributes
        }));
    };

    const handleDeleteAgent = (agentId: string) => {
        setUserProfile(prev => ({
            ...prev,
            styleAgents: (prev.styleAgents || []).filter(a => a.id !== agentId)
        }));
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
            <div className="flex-grow overflow-y-auto custom-scrollbar">
                <div className="p-4 sm:p-8">
                    <div className="max-w-6xl w-full mx-auto mb-8">
                        <header className="mb-8 text-center">
                            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 dark:text-white mb-2">{t('nav_advisor')}</h1>
                            <p className="text-gray-600 dark:text-gray-300">{t('stylingAdvisorPage_tagline')}</p>
                        </header>

                        {/* Tabs */}
                        <div className="flex flex-wrap justify-center border-b border-gray-200 dark:border-gray-700 mb-8">
                            <button onClick={() => setActiveSection('shopping_assistant')} className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 ${activeSection === 'shopping_assistant' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                <SparklesIcon className="w-4 h-4" />
                                {t('nav_assistant_short')}
                                {(!hasAssistantAccess(userProfile)) && <LockIcon className="w-3 h-3 text-gray-400" />}
                            </button>
                            <button onClick={() => setActiveSection('color_analysis')} className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 ${activeSection === 'color_analysis' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {t('stylingAdvisorPage_photoAnalysisSectionTitle')}
                                {isGuest && <LockIcon className="w-3 h-3 text-gray-400" />}
                            </button>
                            <button onClick={() => setActiveSection('plan')} className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 ${activeSection === 'plan' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {t('stylingAdvisorPage_planSectionTitle')}
                                {isGuest && <LockIcon className="w-3 h-3 text-gray-400" />}
                            </button>
                            <button onClick={() => setActiveSection('chat')} className={`px-6 py-3 font-semibold transition-colors ${activeSection === 'chat' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {t('stylingAdvisorPage_chatSection')}
                            </button>
                            <button onClick={() => setActiveSection('agents')} className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 ${activeSection === 'agents' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {t('stylingAdvisorPage_agentsSectionTitle')}
                                {(userProfile.membershipLevel !== 'advanced' && userProfile.membershipLevel !== 'premium') && <LockIcon className="w-3 h-3 text-gray-400" />}
                            </button>
                            <button onClick={() => setActiveSection('history')} className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 ${activeSection === 'history' ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {t('stylingAdvisorPage_historySection')}
                                {isGuest && <LockIcon className="w-3 h-3 text-gray-400" />}
                            </button>
                        </div>

                        {/* Content */}
                        <AnimatePresence mode="wait">
                            {activeSection === 'shopping_assistant' && (
                                <motion.div
                                    key="shopping_assistant"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="max-w-4xl mx-auto"
                                >
                                    {!hasAssistantAccess(userProfile) ? (
                                        <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                                            <LockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('shoppingAssistant_membership_locked')}</h3>
                                            <p className="text-gray-600 dark:text-gray-300 mb-6">{t('feature_locked_premium_generic')}</p>
                                            <button
                                                onClick={() => onNavigate('services')}
                                                className="bg-indigo-600 text-white px-8 py-3 rounded-full font-bold hover:bg-indigo-700 transition-colors shadow-lg"
                                            >
                                                {t('nav_services')}
                                            </button>
                                        </div>
                                    ) : (
                                        <ShoppingAssistantChat
                                            isOpen={true}
                                            onClose={() => { }}
                                            onAddToBasket={(p) => {
                                                // @ts-ignore
                                                if (window.onBasketUpdate) window.onBasketUpdate(p);
                                                alert(t('common_added_to_basket'));
                                            }}
                                            wardrobeItems={[]}
                                            currentOutfit={undefined}
                                            userProfile={userProfile}
                                            onSaveAdvice={onSaveContent}
                                            initialQuery={new URLSearchParams(window.location.search).get('query') || undefined}
                                            isInline={true}
                                        />
                                    )}
                                </motion.div>
                            )}
                            {activeSection === 'chat' && (
                                <motion.div
                                    key="chat"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="grid grid-cols-1 md:grid-cols-3 gap-6"
                                >
                                    <div className="md:col-span-1 space-y-4">
                                        <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('stylingAdvisorPage_agentSelectorLabel')}</h3>
                                        {agents.map(agent => {
                                            let locked = !hasAccess(agent.requiredMembership as any, userProfile.membershipLevel as any, userProfile.membershipExpiry);

                                            // Guest restriction: Guests can only access styleQuestionnaire
                                            if (isGuest && agent.id !== 'styleQuestionnaire') {
                                                locked = true;
                                            }

                                            const isSelected = selectedAgentId === agent.id;
                                            return (
                                                <button
                                                    key={agent.id}
                                                    disabled={locked}
                                                    onClick={() => handleAgentSelect(agent.id)}
                                                    className={`w-full p-4 rounded-xl border text-left transition-all ${isSelected
                                                        ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500 dark:bg-indigo-900/20 dark:border-indigo-400'
                                                        : locked
                                                            ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                                                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                                                        }`}
                                                >
                                                    <div className="flex justify-between items-start">
                                                        <h4 className={`font-bold ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                                            {t(agent.nameKey as any)}
                                                        </h4>
                                                        {locked && <LockIcon className="w-4 h-4 text-gray-400" />}
                                                        {isSelected && !locked && <CheckCircleIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                                                    </div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t(agent.descriptionKey as any)}</p>
                                                    {locked && (
                                                        <p className="text-xs text-indigo-500 mt-1 font-semibold uppercase">
                                                            {isGuest && agent.id !== 'styleQuestionnaire'
                                                                ? t('nav_guestCta')
                                                                : agent.requiredMembership ? t(`stylingAdvisorPage_agent_locked_${agent.requiredMembership}` as any) : ''}
                                                        </p>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="md:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 min-h-[500px] relative overflow-hidden">
                                        {selectedAgentId === 'styleQuestionnaire' ? (
                                            <StyleWizard
                                                userProfile={userProfile}
                                                setUserProfile={setUserProfile}
                                                onComplete={() => setChatOpen(false)}
                                            />
                                        ) : (
                                            <StyleBot
                                                isOpen={chatOpen}
                                                setIsOpen={setChatOpen}
                                                activeAgentId={selectedAgentId}
                                            />
                                        )}
                                        {!chatOpen && selectedAgentId !== 'styleQuestionnaire' && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <button onClick={() => setChatOpen(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-full hover:bg-indigo-700 transition-colors">
                                                    <MessageSquareIcon className="w-5 h-5" /> {t('stylingAdvisorPage_startChat')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {activeSection === 'plan' && (
                                isGuest ? (
                                    <motion.div
                                        key="plan-guest"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="max-w-md mx-auto text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
                                    >
                                        <LockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('guestAccess_title')}</h3>
                                        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('nav_guestCta')}</p>
                                        <button
                                            onClick={() => document.getElementById('onboarding-upload-button')?.click()}
                                            className="bg-indigo-600 text-white px-6 py-2 rounded-full font-bold hover:bg-indigo-700 transition-colors shadow-lg"
                                        >
                                            {t('nav_signUp')}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="plan"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="max-w-2xl mx-auto"
                                    >
                                        <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 text-center">
                                            <FileTextIcon className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('stylingAdvisorPage_planSectionTitle')}</h2>
                                            <p className="text-gray-600 dark:text-gray-300 mb-6">{t('stylingAdvisorPage_planSectionDescription')}</p>

                                            {/* Saved Color Analysis Banner */}
                                            {(userProfile as any).colorAnalysis && (
                                                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl border border-purple-200 dark:border-purple-700/50 text-left">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <PaletteIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                                        <h4 className="font-bold text-purple-800 dark:text-purple-300 text-sm">
                                                            {t('colorAnalysis_title')} — {(userProfile as any).colorAnalysis.season}
                                                        </h4>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                                        {((userProfile as any).colorAnalysis.palette?.powerColors || []).slice(0, 6).map((color: string, i: number) => (
                                                            <span key={i} className="w-6 h-6 rounded-full border border-white/60 shadow-sm" style={{ backgroundColor: color }} title={color} />
                                                        ))}
                                                    </div>
                                                    <p className="text-xs text-purple-600 dark:text-purple-400">
                                                        ✓ Your color profile will be used to personalise your style plan
                                                    </p>
                                                </div>
                                            )}

                                            <div className="flex justify-center gap-4 mb-6">
                                                {(['weekly', 'monthly', 'quarterly'] as PlanDuration[]).map((duration) => {
                                                    const requiredLevel = PLAN_PERMISSIONS[duration];
                                                    const isLocked = requiredLevel !== 'basic' && !hasAccess(requiredLevel === 'premium' ? 'premium' : 'advanced', userProfile.membershipLevel as any, userProfile.membershipExpiry);

                                                    return (
                                                        <button
                                                            key={duration}
                                                            onClick={() => !isLocked && setPlanDuration(duration)}
                                                            disabled={isLocked}
                                                            className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${isLocked
                                                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed border border-gray-200 dark:border-gray-700'
                                                                : planDuration === duration
                                                                    ? 'bg-indigo-600 text-white'
                                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                                }`}
                                                        >
                                                            {t(`stylingAdvisorPage_planDuration_${duration}` as any)}
                                                            {isLocked && <LockIcon className="w-3 h-3" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                onClick={handleGeneratePlan}
                                                disabled={isGeneratingPlan}
                                                className="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center mx-auto gap-2"
                                            >
                                                {isGeneratingPlan ? <Spinner className="w-5 h-5" /> : <SparklesIcon className="w-5 h-5" />}
                                                {isGeneratingPlan ? t('stylingAdvisorPage_thinking') : t('stylingAdvisorPage_generatePlan')}
                                            </button>

                                            {planError && <p className="text-red-500 dark:text-red-400 mt-4">{planError}</p>}
                                        </div>

                                        {generatedPlan && (
                                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-gray-50 dark:bg-gray-900 p-8 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700/50">
                                                <div className="prose prose-lg dark:prose-invert max-w-none mb-6 text-gray-900 dark:text-gray-100 leading-relaxed" dangerouslySetInnerHTML={{ __html: generatedPlan }} />
                                                <div className="flex justify-end gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
                                                    <button onClick={handleGeneratePlan} className="text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 flex items-center gap-1">
                                                        <RotateCcwIcon className="w-4 h-4" /> {t('stylingAdvisorPage_regeneratePlan')}
                                                    </button>
                                                    <button
                                                        onClick={handleSavePlan}
                                                        disabled={isPlanSaved}
                                                        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                                    >
                                                        <CheckCircleIcon className="w-4 h-4" /> {isPlanSaved ? t('stylingAdvisorPage_saveSuccess') : t('myStylesPage_savePlan')}
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )
                            )}

                            {activeSection === 'color_analysis' && (
                                isGuest ? (
                                    <motion.div
                                        key="analysis-guest"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="max-w-md mx-auto text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
                                    >
                                        <LockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('guestAccess_title')}</h3>
                                        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('nav_guestCta')}</p>
                                        <button
                                            onClick={() => document.getElementById('onboarding-upload-button')?.click()}
                                            className="bg-indigo-600 text-white px-6 py-2 rounded-full font-bold hover:bg-indigo-700 transition-colors shadow-lg"
                                        >
                                            {t('nav_signUp')}
                                        </button>
                                    </motion.div>
                                ) : !hasAccess('premium', userProfile.membershipLevel as any, userProfile.membershipExpiry) ? (
                                    <motion.div
                                        key="analysis-locked"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="max-w-md mx-auto text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
                                    >
                                        <LockIcon className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('colorAnalysis_title')}</h3>
                                        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('feature_locked_premium_generic')}</p>
                                        <button
                                            onClick={() => onNavigate('services')}
                                            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-full font-bold hover:opacity-90 transition-opacity shadow-lg flex items-center mx-auto gap-2"
                                        >
                                            <SparklesIcon className="w-5 h-5" />
                                            {t('nav_services')}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="analysis"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="max-w-2xl mx-auto"
                                    >
                                        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 text-center">
                                            <PaletteIcon className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('colorAnalysis_title')}</h2>
                                            <p className="text-gray-600 dark:text-gray-300 mb-8">{t('stylingAdvisorPage_photoAnalysisSectionDescription')}</p>

                                            {/* Show saved color analysis results if they exist */}
                                            {(userProfile as any).colorAnalysis && (
                                                <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50 dark:from-purple-900/20 dark:via-indigo-900/20 dark:to-pink-900/20 rounded-2xl border border-purple-200 dark:border-purple-700/50 text-left">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <div className="bg-purple-100 dark:bg-purple-900/40 p-2 rounded-full">
                                                            <CheckCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-bold text-purple-800 dark:text-purple-300">
                                                                {(userProfile as any).colorAnalysis.season}
                                                            </h3>
                                                            <p className="text-xs text-purple-500 dark:text-purple-400">Your saved color profile</p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                                        <div>
                                                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Skin Tone</p>
                                                            <p className="text-sm text-gray-800 dark:text-gray-200">{(userProfile as any).colorAnalysis.characteristics?.skinTone || '—'}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Hair / Eyes</p>
                                                            <p className="text-sm text-gray-800 dark:text-gray-200">
                                                                {(userProfile as any).colorAnalysis.characteristics?.hairColor || '—'} / {(userProfile as any).colorAnalysis.characteristics?.eyeColor || '—'}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="mb-4">
                                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Power Colors</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {((userProfile as any).colorAnalysis.palette?.powerColors || []).map((color: string, i: number) => (
                                                                <span key={i} className="w-8 h-8 rounded-lg border-2 border-white/80 dark:border-gray-700 shadow-sm" style={{ backgroundColor: color }} title={color} />
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {((userProfile as any).colorAnalysis.palette?.avoidColors || []).length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Colors to Avoid</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {((userProfile as any).colorAnalysis.palette?.avoidColors || []).map((color: string, i: number) => (
                                                                    <span key={i} className="w-8 h-8 rounded-lg border-2 border-white/80 dark:border-gray-700 shadow-sm relative" style={{ backgroundColor: color }} title={color}>
                                                                        <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold drop-shadow">✕</span>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <button
                                                onClick={() => setIsAnalysisModalOpen(true)}
                                                className="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center mx-auto gap-2"
                                            >
                                                <SparklesIcon className="w-5 h-5" />
                                                {(userProfile as any).colorAnalysis ? t('stylingAdvisorPage_photoAnalysisButton') + ' ↻' : t('stylingAdvisorPage_photoAnalysisButton')}
                                            </button>
                                        </div>
                                    </motion.div>
                                )
                            )}

                            {activeSection === 'agents' && (
                                !hasAccess('advanced', userProfile.membershipLevel as any, userProfile.membershipExpiry) ? (
                                    <motion.div
                                        key="agents-locked"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="max-w-md mx-auto text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
                                    >
                                        <LockIcon className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('stylingAdvisorPage_agentsSectionTitle')}</h3>
                                        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('feature_locked_advanced_generic')}</p>
                                        <button
                                            onClick={() => onNavigate('services')}
                                            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-full font-bold hover:opacity-90 transition-opacity shadow-lg flex items-center mx-auto gap-2"
                                        >
                                            <SparklesIcon className="w-5 h-5" />
                                            {t('nav_services')}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="agents"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="max-w-3xl mx-auto"
                                    >
                                        <div className="flex justify-between items-center mb-6">
                                            <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white">{t('stylingAdvisorPage_agentsDashboardTitle')}</h2>
                                            <button
                                                onClick={() => {
                                                    if (checkStyleAgentQuota(userProfile)) {
                                                        setIsAgentModalOpen(true);
                                                    } else {
                                                        alert(t('styleAgent_quotaExceeded'));
                                                    }
                                                }}
                                                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                                            >
                                                <PlusIcon className="w-4 h-4" /> {t('stylingAdvisorPage_createAgent')}
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {userProfile.styleAgents && userProfile.styleAgents.length > 0 ? (
                                                userProfile.styleAgents.map(agent => (
                                                    <div key={agent.id} className="bg-gray-50 dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm relative group">
                                                        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => handleRunAgent(agent.id)}
                                                                disabled={isRunningAgents[agent.id]}
                                                                className="text-gray-400 hover:text-indigo-600 disabled:opacity-50"
                                                                title={t('styleAgent_runAgent')}
                                                            >
                                                                {isRunningAgents[agent.id] ? <Spinner className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                                                            </button>
                                                            <button onClick={() => handleDeleteAgent(agent.id)} className="text-gray-400 hover:text-red-500" title={t('common_delete')}>
                                                                <Trash2Icon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                        <div className="flex items-center gap-3 mb-3">
                                                            <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-full text-indigo-600 dark:text-indigo-400">
                                                                <BotIcon className="w-6 h-6" />
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-gray-900 dark:text-white">{agent.name}</h3>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{agent.frequency}</p>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                                            <p><span className="font-semibold">Season:</span> {agent.targetSeason}</p>
                                                            <p><span className="font-semibold">Focus:</span> {agent.focusArea}</p>
                                                            <div className="flex flex-col gap-1 mt-2">
                                                                <p className="text-xs text-gray-400">Next run: {new Date(agent.nextRun).toLocaleDateString()}</p>
                                                                {agent.lastRun && <p className="text-xs text-gray-400">Last run: {new Date(agent.lastRun).toLocaleDateString()}</p>}
                                                                {agent.lastEmailSent && <p className="text-xs text-indigo-500 dark:text-indigo-400 flex items-center gap-1">
                                                                    <MailIcon className="w-3 h-3" /> {t('styleAgent_lastEmail', { date: new Date(agent.lastEmailSent).toLocaleDateString() })}
                                                                </p>}
                                                                {agent.cacheIntervalsLeft !== undefined && agent.cacheIntervalsLeft > 0 && (
                                                                    <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                                                        <CheckCircleIcon className="w-3 h-3" /> {t('styleAgent_cacheActive', { intervals: agent.cacheIntervalsLeft })}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="col-span-full text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                                                    <BotIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                                    <p className="text-gray-500 dark:text-gray-400">{t('stylingAdvisorPage_noAgents')}</p>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )
                            )}

                            {activeSection === 'history' && (
                                isGuest ? (
                                    <motion.div
                                        key="history-guest"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="max-w-md mx-auto text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700"
                                    >
                                        <LockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('guestAccess_title')}</h3>
                                        <p className="text-gray-600 dark:text-gray-300 mb-6">{t('nav_guestCta')}</p>
                                        <button
                                            onClick={() => document.getElementById('onboarding-upload-button')?.click()}
                                            className="bg-indigo-600 text-white px-6 py-2 rounded-full font-bold hover:bg-indigo-700 transition-colors shadow-lg"
                                        >
                                            {t('nav_signUp')}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="history"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="max-w-4xl mx-auto space-y-6"
                                    >
                                        <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-white px-2">{t('stylingAdvisorPage_historySection')}</h2>
                                        {savedContent.filter(c => c.type === 'search').length > 0 ? (
                                            savedContent.filter(c => c.type === 'search').sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(search => (
                                                <div key={search.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm relative group hover:shadow-md transition-shadow">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full text-blue-600 dark:text-blue-400">
                                                                <SearchIcon className="w-5 h-5" />
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-gray-900 dark:text-white">{search.title}</h3>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(search.createdAt).toLocaleString()}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                                        {search.content}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                                                <SearchIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                                <p className="text-gray-500 dark:text-gray-400">{t('stylingAdvisorPage_noSearches')}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                )
                            )}
                        </AnimatePresence>
                    </div>

                    <ColorAnalysisModal
                        isOpen={isAnalysisModalOpen}
                        onClose={() => setIsAnalysisModalOpen(false)}
                        result={(userProfile as any).colorAnalysis || null}
                        savedOutfits={savedOutfits}
                        onSaveToProfile={handleSaveColorAnalysis}
                    />

                    <StyleAgentModal
                        isOpen={isAgentModalOpen}
                        onClose={() => setIsAgentModalOpen(false)}
                        onSaveAgent={handleSaveAgent}
                    />

                    <div className="mt-16">
                        <Footer onNavigate={onNavigate} />
                    </div>
                </div>
            </div>
        </div >
    );
};

export default StylingAdvisorPage;
